<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

const ACCEPTED_FILE_TYPES =
  'image/*, audio/*, video/*, application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, text/plain';
const MAX_FILE_SIZE_MB = 40;

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edattachments: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      existingAttachments: [...(this.edattachments || [])],
      newFiles: [],
      removedAttachmentIds: [],
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    acceptedFileTypes() {
      return ACCEPTED_FILE_TYPES;
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.newFiles = [];
      this.removedAttachmentIds = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    onFileChange(event) {
      const files = Array.from(event.target.files);
      files.forEach(file => {
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          useAlert(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
          return;
        }
        this.newFiles.push(file);
      });
      event.target.value = '';
    },
    removeNewFile(index) {
      this.newFiles.splice(index, 1);
    },
    removeExistingAttachment(attachment) {
      this.removedAttachmentIds.push(attachment.blob_id);
      this.existingAttachments = this.existingAttachments.filter(
        a => a.blob_id !== attachment.blob_id
      );
    },
    getFileIcon(file) {
      const type = file.file_type || file.type || '';
      if (type.startsWith('image/') || type.startsWith('image')) return '🖼️';
      if (type.startsWith('audio/') || type.startsWith('audio')) return '🎵';
      if (type.startsWith('video/') || type.startsWith('video')) return '🎬';
      return '📎';
    },
    formatFileSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    editCannedResponse() {
      this.editCanned.showLoading = true;
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          files: this.newFiles,
          removedAttachmentIds: this.removedAttachmentIds,
        })
        .then(() => {
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <!-- Attachments Section -->
        <div class="w-full mt-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('CANNED_MGMT.ATTACHMENTS.LABEL') }}
          </label>
          <div class="flex flex-wrap gap-2 mt-1">
            <!-- Existing attachments -->
            <div
              v-for="attachment in existingAttachments"
              :key="attachment.blob_id"
              class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-n-alpha-2 border border-n-weak text-xs"
            >
              <span>{{ getFileIcon(attachment) }}</span>
              <span class="max-w-[150px] truncate">{{
                attachment.filename
              }}</span>
              <span class="text-n-slate-10">{{
                formatFileSize(attachment.byte_size)
              }}</span>
              <button
                type="button"
                class="ml-1 text-n-slate-10 hover:text-n-ruby-9"
                @click="removeExistingAttachment(attachment)"
              >
                <fluent-icon icon="dismiss" size="12" />
              </button>
            </div>
            <!-- New files -->
            <div
              v-for="(file, index) in newFiles"
              :key="`new-${index}`"
              class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-n-brand/10 border border-n-brand/30 text-xs"
            >
              <span>{{ getFileIcon(file) }}</span>
              <span class="max-w-[150px] truncate">{{ file.name }}</span>
              <span class="text-n-slate-10">{{
                formatFileSize(file.size)
              }}</span>
              <button
                type="button"
                class="ml-1 text-n-slate-10 hover:text-n-ruby-9"
                @click="removeNewFile(index)"
              >
                <fluent-icon icon="dismiss" size="12" />
              </button>
            </div>
          </div>
          <label
            class="inline-flex items-center gap-1.5 px-3 py-1.5 mt-2 text-xs font-medium rounded-lg cursor-pointer bg-n-alpha-2 border border-n-weak text-n-slate-12 hover:bg-n-alpha-3 transition-colors"
          >
            <fluent-icon icon="attach" size="14" />
            {{ $t('CANNED_MGMT.ATTACHMENTS.ADD') }}
            <input
              type="file"
              multiple
              :accept="acceptedFileTypes"
              class="hidden"
              @change="onFileChange"
            />
          </label>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];

  p {
    @apply text-base;
  }
}
</style>
